import { useContext, useEffect } from 'react'
import ErrorScreen from '../../Misc/ErrorScreen'
import { customerCreditType } from '../../types'
import {
  Button,
  Chip,
  Grid,
  Link as LinkMui,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { dataContext } from '../..'
import { Link as LinkRouter } from 'react-router-dom'
import dayjs from 'dayjs'
import { DatePicker, getRepCodeName } from '../../helper'

function CreditList(props: {
  creditList: (customerCreditType & {
    customer_name: string
    job_name: string
  })[]
  creditCount: number
  filters: { [index: string]: string | number }
  searchError: boolean
  initialLoad: boolean
  listLoading: boolean
  handleSearch: (filters: { [index: string]: string | number }) => void
  setFilters: (filters: { [index: string]: string | number }) => void
}) {
  const { user, salesReps } = useContext(dataContext)

  useEffect(() => {
    if (user.active_flag) {
      props.handleSearch(props.filters)
    }
  }, [user])

  if (props.searchError) {
    return (
      <div className="customer-list-error-container">
        <ErrorScreen
          errorText="Something went wrong while searching. Please try again or contact support if the error persists."
          title="Search Failed"
        />
      </div>
    )
  }

  return (
    <TableContainer className="customer-list-table">
      <div className="customer-pagination-header">
        <div className="customer-list-title">
          {props.initialLoad && props.listLoading ? (
            <Skeleton width={150} height={32} />
          ) : (
            <Typography variant="h6">Customer Credits</Typography>
          )}
        </div>
        {props.initialLoad && props.listLoading ? (
          <Skeleton width={300} height={32} />
        ) : (
          <TablePagination
            component="div"
            count={props.creditCount}
            page={props.filters.page as number}
            rowsPerPage={props.filters.limit as number}
            onPageChange={(_e, newPage) => {
              const newFilters = { ...props.filters, page: newPage }
              props.setFilters(newFilters)
              props.handleSearch(newFilters)
            }}
            onRowsPerPageChange={e => {
              const newFilters = { ...props.filters, limit: parseInt(e.target.value) }
              props.setFilters(newFilters)
              props.handleSearch(newFilters)
            }}
          />
        )}
      </div>
      <Table stickyHeader color="primary">
        <TableHead>
          {props.initialLoad && props.listLoading ? (
            <TableRow>
              {Array.from({ length: 8 }).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton className="skeleton-header" />
                </TableCell>
              ))}
            </TableRow>
          ) : (
            <TableRow>
              <TableCell width="5%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('ref_number')}
                  direction={props.filters.sort === 'ref_number' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'ref_number' ? 'ref_number desc' : 'ref_number',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Credit #
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('customer_name')}
                  direction={props.filters.sort === 'customer_name' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'customer_name' ? 'customer_name desc' : 'customer_name',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Customer Name
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%" align="right">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('customer_po_number')}
                  direction={props.filters.sort === 'customer_po_number' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort:
                        props.filters.sort === 'customer_po_number' ? 'customer_po_number desc' : 'customer_po_number',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Customer PO Number
                </TableSortLabel>
              </TableCell>
              <TableCell width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('rep_code')}
                  direction={props.filters.sort === 'rep_code' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'rep_code' ? 'rep_code desc' : 'rep_code',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Sales Rep
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('txn_date')}
                  direction={props.filters.sort === 'txn_date' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'txn_date' ? 'txn_date desc' : 'txn_date',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Credit Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('due_date')}
                  direction={props.filters.sort === 'due_date' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'due_date' ? 'due_date desc' : 'due_date',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="7%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('total_amount')}
                  direction={props.filters.sort === 'total_amount' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'total_amount' ? 'total_amount desc' : 'total_amount',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Total Amount
                </TableSortLabel>
              </TableCell>
              <TableCell width="5%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('is_paid')}
                  direction={props.filters.sort === 'is_paid' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'is_paid' ? 'is_paid desc' : 'is_paid',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Paid
                </TableSortLabel>
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {props.listLoading
            ? Array.from({ length: props.filters.limit as number }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: 8 }).map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : props.creditList.map(credit => (
                <TableRow key={credit.customer_credit_txn_id}>
                  <TableCell>
                    <LinkMui to={`/customer/credit/${credit.customer_credit_txn_id}`} component={LinkRouter}>
                      {credit.ref_number}
                    </LinkMui>
                  </TableCell>
                  <TableCell>{credit.customer_name ? credit.customer_name : '-'}</TableCell>
                  <TableCell align="right">{credit.customer_po_number ? credit.customer_po_number : '-'}</TableCell>
                  <TableCell>{`${credit.rep_code} ${getRepCodeName(
                    salesReps.find(rep => rep.initial === credit.rep_code),
                  )}`}</TableCell>
                  <TableCell align="right">
                    {credit.txn_date ? dayjs.utc(credit.txn_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {credit.due_date ? dayjs.utc(credit.due_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {credit.total_amount
                      ? credit.total_amount.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={credit.is_paid ? 'Paid' : 'Unpaid'}
                      color={credit.is_paid ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <TablePagination
        className="customer-pagination-footer"
        component="div"
        count={props.creditCount}
        page={props.filters.page as number}
        rowsPerPage={props.filters.limit as number}
        onPageChange={(_e, newPage) => {
          const newFilters = { ...props.filters, page: newPage }
          props.setFilters(newFilters)
          props.handleSearch(newFilters)
        }}
        onRowsPerPageChange={e => {
          const newFilters = { ...props.filters, limit: parseInt(e.target.value) }
          props.setFilters(newFilters)
          props.handleSearch(newFilters)
        }}
      />
    </TableContainer>
  )
}

export function CreditSearch(props: {
  initialLoad: boolean
  listLoading: boolean
  filters: { [index: string]: string | number }
  setFilters: (filters: { [index: string]: string | number }) => void
  handleSearch: (filters: { [index: string]: string | number }) => void
}) {
  const handleClear = () => {
    const newFilters = {
      page: 0,
      limit: 25,
      sort: 'txn_date desc',
      customerJobName: '',
      creditNumber: '',
      fromDate: '',
      toDate: '',
      invoiceNumber: '',
    }

    props.setFilters(newFilters)
    props.handleSearch(newFilters)
  }

  if (props.initialLoad && props.listLoading) {
    return (
      <form className="customer-advanced-search-form">
        <Grid container spacing={'17px'}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <Grid size={3} className="customer-advanced-search-grid" key={idx}>
              <Skeleton width={120} height={24} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={'17px'}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <Grid size={idx === 3 || idx === 4 ? 1.5 : 3} className="customer-advanced-search-grid" key={idx}>
              <Skeleton variant="rounded" width="100%" height={40} />
            </Grid>
          ))}
        </Grid>
        <div className="customer-advanced-search-buttons">
          <Button
            size="large"
            variant="outlined"
            onClick={e => {
              e.preventDefault()
              handleClear()
            }}
            loading={props.listLoading}
          >
            Clear
          </Button>
          <Button
            size="large"
            variant="contained"
            type="submit"
            onClick={e => {
              e.preventDefault()
              props.handleSearch(props.filters)
            }}
            loading={props.listLoading}
          >
            Search
          </Button>
        </div>
      </form>
    )
  }

  return (
    <form className="customer-advanced-search-form">
      <Grid container spacing={'17px'}>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Customer/Job Name:</Typography>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Credit Reference Number:</Typography>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Transaction Date:</Typography>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Invoice Number:</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={'17px'}>
        <Grid size={3} className="customer-advanced-search-grid">
          <TextField
            variant="outlined"
            size="small"
            label="Customer/Job Name"
            value={props.filters.customerJobName}
            fullWidth
            onChange={e => {
              props.setFilters({
                ...props.filters,
                customerJobName: e.target.value,
              })
            }}
          />
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <TextField
            variant="outlined"
            size="small"
            label="Credit Reference Number"
            value={props.filters.creditNumber}
            fullWidth
            onChange={e => {
              props.setFilters({
                ...props.filters,
                creditNumber: e.target.value,
              })
            }}
          />
        </Grid>

        <Grid size={3} className="customer-advanced-search-grid">
          <div className="customer-advanced-search-date-group">
            <DatePicker
              size="small"
              label="From Date"
              value={props.filters.fromDate ? dayjs.utc(props.filters.fromDate) : null}
              onChange={e => {
                props.setFilters({
                  ...props.filters,
                  fromDate: e?.format('YYYY-MM-DD') ?? '',
                })
              }}
            />
            <DatePicker
              size="small"
              label="To Date"
              value={props.filters.toDate ? dayjs.utc(props.filters.toDate) : null}
              onChange={e => {
                props.setFilters({
                  ...props.filters,
                  toDate: e?.format('YYYY-MM-DD') ?? '',
                })
              }}
            />
          </div>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <TextField
            variant="outlined"
            size="small"
            label="Invoice Number"
            value={props.filters.invoiceNumber}
            fullWidth
            onChange={e => {
              props.setFilters({
                ...props.filters,
                invoiceNumber: e.target.value,
              })
            }}
          />
        </Grid>
      </Grid>
      <div className="customer-advanced-search-buttons">
        <Button
          size="large"
          variant="outlined"
          onClick={e => {
            e.preventDefault()
            handleClear()
          }}
          loading={props.listLoading && props.initialLoad}
        >
          Clear
        </Button>
        <Button
          size="large"
          variant="contained"
          type="submit"
          onClick={e => {
            e.preventDefault()
            props.handleSearch(props.filters)
          }}
          loading={props.listLoading && props.initialLoad}
        >
          Search
        </Button>
      </div>
    </form>
  )
}

export default CreditList
